import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import fallbackLogo from '../../imports/logo-horizontal.png'
import {
    type ImageSettingValue,
    type SettingItem,
    settingsService,
    settingValue,
} from '@/app/services/settings.service'

const DEFAULT_PRIMARY = '#3B7D3B'
const DEFAULT_SECONDARY = '#2563EB'
const DEFAULT_ACCENT = '#aefeeaff'
const DEFAULT_COMPANY_NAME = 'Farmacia Salud Plus'

type BrandingMode = 'private' | 'public'

interface UseSettingsBrandingOptions {
    mode: BrandingMode
    sucursalId?: number | null
    enabled?: boolean
}

export interface SettingsBranding {
    companyName: string
    logoMainUrl: string
    logoEmailUrl: string | null
    faviconUrl: string | null
    primaryColor: string
    secondaryColor: string
    accentColor: string
}

import { themeService } from '../services/theme.service'

export function useSettingsBranding({
    mode,
    sucursalId = null,
    enabled = true,
}: UseSettingsBrandingOptions) {
    const query = useQuery({
        queryKey: mode === 'private' ? ['settings'] : ['settings', 'public', sucursalId],
        queryFn: () => mode === 'private'
            ? settingsService.getSettings()
            : settingsService.getPublicSettings(sucursalId as number),
        enabled: enabled && (mode === 'private' || Boolean(sucursalId)),
        staleTime: 60_000,
    })

    const themeQuery = useQuery({
        queryKey: ['activeTheme', sucursalId],
        queryFn: () => themeService.getCurrentTheme(),
        enabled: enabled && mode === 'private',
        staleTime: 60_000,
    })

    const settings = query.data ?? []
    const branding = useMemo(() => resolveBranding(settings), [settings])

    useEffect(() => {
        applyTheme(branding, themeQuery.data)
    }, [branding, themeQuery.data])

    return {
        ...query,
        settings,
        branding,
        activeTheme: themeQuery.data,
    }
}

export function resolveBranding(settings: SettingItem[]): SettingsBranding {
    const logoMain = settingValue<ImageSettingValue | null>(settings, 'logo_main', null)
    const logoEmail = settingValue<ImageSettingValue | null>(settings, 'logo_email', null)
    const favicon = settingValue<ImageSettingValue | null>(settings, 'favicon', null)

    return {
        companyName: settingValue(settings, 'company_name', DEFAULT_COMPANY_NAME),
        logoMainUrl: logoMain?.url || fallbackLogo,
        logoEmailUrl: logoEmail?.url || null,
        faviconUrl: favicon?.url || null,
        primaryColor: settingValue(settings, 'theme.primary_color', DEFAULT_PRIMARY),
        secondaryColor: settingValue(settings, 'theme.secondary_color', DEFAULT_SECONDARY),
        accentColor: settingValue(settings, 'theme.accent_color', DEFAULT_ACCENT),
    }
}

function applyTheme(branding: SettingsBranding, activeTheme?: any): void {
    const root = document.documentElement

    root.style.setProperty('--primary', branding.primaryColor)
    root.style.setProperty('--ring', branding.primaryColor)
    root.style.setProperty('--sidebar-primary', branding.primaryColor)
    root.style.setProperty('--chart-1', branding.primaryColor)
    root.style.setProperty('--secondary', branding.secondaryColor)
    root.style.setProperty('--sidebar-accent', branding.secondaryColor)
    root.style.setProperty('--chart-2', branding.secondaryColor)
    root.style.setProperty('--accent', branding.accentColor)
    root.style.setProperty('--chart-4', branding.accentColor)

    if (activeTheme?.properties) {
        activeTheme.properties.forEach((prop: any) => {
            root.style.setProperty(`--${prop.key}`, prop.value.value)
        })
    }

    document.title = `${branding.companyName || DEFAULT_COMPANY_NAME}`

    if (branding.faviconUrl) {
        setFavicon(branding.faviconUrl)
    }
}

function setFavicon(url: string): void {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')

    if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
    }

    link.href = url
}
